// Authentication state backed by Authentik (OIDC Authorization Code + PKCE).
// `user` is the aidb account profile from /api/v1/me; the OIDC session itself lives in
// oidc-client-ts (sessionStorage).
import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import * as AccountAPI from '../api/AccountApi';
import * as oidc from '../auth/oidc';
import * as legacy from '../auth/legacy';
import * as social from '../auth/social';

const AuthContext = createContext(null);

export const AuthProvider = ({children}) => {
  const [user, setUser] = useState(null);
  const [checkIsLogin, setCheckIsLogin] = useState(false);

  const loadProfile = useCallback(async () => {
    const session = legacy.getSession() || (await oidc.getUserManager().getUser());
    if (!session || session.expired) {
      setUser(null);
      return null;
    }
    try {
      const profile = await AccountAPI.getAccountData();
      setUser(profile);
      return profile;
    } catch (err) {
      setUser(null);
      console.warn('Loading the account profile failed:', err.message);
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname === oidc.callbackPath) {
      // The callback route completes sign-in and reloads the profile itself.
      setCheckIsLogin(true);
      return;
    }
    loadProfile().finally(() => setCheckIsLogin(true));
  }, [loadProfile]);

  // A first-party session ends on its own when the token expires.
  useEffect(() => {
    if (!user) return undefined;
    const session = legacy.getSession();
    if (!session) return undefined;
    const timer = setTimeout(
      () => {
        legacy.clearSession();
        setUser(null);
      },
      Math.max(0, session.expiresAt - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      checkIsLogin,
      /** Redirects to Authentik; `returnTo` is restored after the callback. */
      login: (returnTo = '/') => oidc.login(returnTo),
      /** Legacy credentials (email/phone/username + password); resolves once the profile loads. */
      loginWithPassword: async (identifier, password) => {
        await legacy.loginWithPassword(identifier, password);
        const profile = await loadProfile();
        if (!profile) {
          legacy.clearSession();
          throw new Error('Incorrect email, phone, username or password.');
        }
        return profile;
      },
      /**
       * Provider SDK token -> POST /api/v1/auth/social (same session as a password sign-in);
       * resolves once the profile loads, rejects with a user-facing message.
       */
      loginWithSocial: async (request) => {
        await social.socialLogin(request);
        const profile = await loadProfile();
        if (!profile) {
          legacy.clearSession();
          throw new Error(social.socialMessages(request.provider).generic);
        }
        return profile;
      },
      completeLogin: async () => {
        const returnTo = await oidc.completeLogin();
        await loadProfile();
        setCheckIsLogin(true);
        return returnTo;
      },
      logout: async () => {
        // On success the sign-out navigates the whole page away (a single navigation), so the
        // user stays in context until then: clearing it first would let the route guard push
        // /landing in-app while the revocation call is still in flight.
        try {
          await oidc.logout();
        } catch (err) {
          setUser(null);
          throw err;
        }
      },
      getAccountData: loadProfile,
      updateAccountData: async (accountData) => {
        const updated = await AccountAPI.updateAccountData(accountData);
        setUser((prev) => ({...prev, ...updated}));
        return updated;
      },
    }),
    [user, checkIsLogin, loadProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export const useAuth = () => useContext(AuthContext);
